<script lang="ts">
    // Versão nova do payments-link-select, no mesmo formato do service-select-2: a lista abre dentro de um
    // Zinput (ZinputSearchTrigger) e cada resultado ocupa duas linhas — descrição em cima, valor e
    // parcelamento embaixo. O componente antigo (payments-link-select.svelte) segue existindo para as
    // telas que ainda não migraram.
    import Fa from 'svelte-fa'
    import { faThList, faTimes } from '@fortawesome/free-solid-svg-icons'
    import { createEventDispatcher, onMount } from 'svelte/internal'
    import { PaymentLink } from '../../modules/paymentsApi/payment-links/payment-link.entity'
    import { PurchaseType, isSubscriptionType } from '../../modules/paymentsApi/purchases/purchase.enum'
    import Zinput from '../_common/z/zinput.svelte'
    import ZinputSearchTrigger from '../_common/z/zinput/zinput-search-trigger.svelte'
    import type { GridButton } from '../_common/z/zgrid'

    const dispatch = createEventDispatcher()

    export let paymentLink: PaymentLink = null
    export let disabled: boolean = false
    export let label = ''
    export let category = undefined
    export let allowSingleCharge = false

    // Restringe a lista pelo que o cadastro FAZ. Quem consome usa as constantes CHARGE_TYPES /
    // SUBSCRIPTION_TYPES, para a fronteira entre cobrança e assinatura viver num lugar só.
    // Sem a prop, lista tudo.
    export let purchaseTypes: string[] = null

    let query = ''
    // a api devolve a lista inteira de uma vez, então a busca é local — sem ida ao servidor a cada tecla
    let paymentLinks: PaymentLink[] = []
    let results: PaymentLink[] = []

    async function loadPaymentLinks() {
        paymentLinks = await window.api.payments.paymentLinks.getAll({ category: category })
        if (purchaseTypes?.length) {
            paymentLinks = paymentLinks.filter((el) => purchaseTypes.includes(el.purchaseType))
        }
        paymentLinks.sort((a, b) => (a.category < b.category ? 1 : -1))
        search(query)
    }

    function search(q: string) {
        const term = (q || '').trim().toLowerCase()
        if (!term) {
            results = paymentLinks
            return
        }
        results = paymentLinks.filter((el) => friendlyLabel(el).toLowerCase().includes(term))
    }

    // O agrupamento segue o mesmo critério do rótulo — o que o cadastro faz, não como foi salvo
    $: assinaturas = results.filter((el) => isSubscriptionType(el.purchaseType))
    $: cobrancas = results.filter((el) => !isSubscriptionType(el.purchaseType))

    function select(item: PaymentLink, close?: () => void) {
        paymentLink = item
        query = ''
        search('')
        close?.()
        dispatch('change')
    }

    function clear() {
        paymentLink = null
        dispatch('change')
    }

    onMount(async () => {
        await loadPaymentLinks()
        if (paymentLink?.paymentLinkId) {
            paymentLink = paymentLinks.find((el) => el.paymentLinkId === paymentLink.paymentLinkId)
        }
    })

    function friendlyLabel(pl: PaymentLink) {
        if (!pl) return ''
        return pl.purchaseDescription || ''
    }

    // O rótulo sai do que o cadastro FAZ, e a fronteira mora em isSubscriptionType(): só a
    // recorrente contínua é assinatura. O carnê (recorrente fixa) é venda fracionada, e aparece
    // como cobrança — que é como ele se comporta.
    function categoriaReal(pl: PaymentLink): 'subscription' | 'link' {
        return isSubscriptionType(pl?.purchaseType) ? 'subscription' : 'link'
    }

    function friendlyCategory(pl: PaymentLink) {
        return categoriaReal(pl) === 'subscription' ? 'Assinatura' : 'Cobrança'
    }

    function friendlyValue(pl: PaymentLink) {
        return window.api.utils.utils.toReal(pl?.purchaseTotalValue)
    }

    // mesmo critério da listagem de cobranças: contínua não tem parcelamento a exibir
    function friendlyInstallments(pl: PaymentLink) {
        if (pl?.purchaseType === PurchaseType.recurring_continuous) return '-'
        return 'até ' + (pl?.purchaseTotalInstallments || 1) + 'x'
    }

    const btnClear: GridButton = {
        label: '',
        icon: faTimes,
        callback: clear,
    }
</script>

{#if paymentLink && paymentLink.paymentLinkId}
    <Zinput type="text" {label} disabled={true} value={friendlyLabel(paymentLink)} acts={disabled ? [] : [btnClear]} />
{:else}
    <ZinputSearchTrigger bind:query {label} {disabled} minLength={0} {search} let:close>
        {#if results.length === 0 && !allowSingleCharge}
            <div class="p-2 text-muted">Não encontrado.</div>
        {:else}
            {#if allowSingleCharge}
                <button class="results_row" on:click={() => select(null, close)}>
                    <div class="-label">
                        <div class="-label_name">Cobrança avulsa</div>
                    </div>
                </button>
            {/if}

            {#if (!category || category === 'subscription') && assinaturas.length > 0}
                <div class="results_title"><Fa icon={faThList} /> Assinaturas</div>
                {#each assinaturas as item}
                    <button class="results_row" on:click={() => select(item, close)}>
                        <div class="-label">
                            <div class="-label_name">{friendlyLabel(item)}</div>
                            <div class="badge badge_category-{categoriaReal(item)}">{friendlyCategory(item)}</div>
                        </div>
                        <div class="-details">
                            <div class="-value"><span>Valor:</span><span>{friendlyValue(item)}</span></div>
                            <div class="-installments"><span>Parcelas:</span><span>{friendlyInstallments(item)}</span></div>
                        </div>
                    </button>
                {/each}
            {/if}

            {#if (!category || category === 'link') && cobrancas.length > 0}
                <div class="results_title"><Fa icon={faThList} /> Cobranças</div>
                {#each cobrancas as item}
                    <button class="results_row" on:click={() => select(item, close)}>
                        <div class="-label">
                            <div class="-label_name">{friendlyLabel(item)}</div>
                            <div class="badge badge_category-{categoriaReal(item)}">{friendlyCategory(item)}</div>
                        </div>
                        <div class="-details">
                            <div class="-value"><span>Valor:</span><span>{friendlyValue(item)}</span></div>
                            <div class="-installments"><span>Parcelas:</span><span>{friendlyInstallments(item)}</span></div>
                        </div>
                    </button>
                {/each}
            {/if}
        {/if}
    </ZinputSearchTrigger>
{/if}

<style>
    .results_title {
        font-size: 18px;
        font-weight: 300;
        padding: 12px 4px;
        background-color: #88888805;
    }
    :global(.results_title svg) {
        margin-right: 4px;
    }
    .results_row {
        display: flex;
        width: 100%;
        padding: 4px 4px 4px 8px;
        text-align: left;
        border: 0px;
        background: transparent;
        color: inherit;
        flex-direction: column;
        border-top: 1px solid #99999922;
        background-color: #99999915;
    }
    .results_row:hover {
        background-color: #ffffff12;
    }
    :global(.theme_light) .results_row:hover {
        background-color: #00000010;
    }
    .results_row .-label {
        flex-grow: 1;
        display: flex;
        align-items: center;
        opacity: 0.9;
    }
    .results_row .-label_name {
        flex-grow: 1;
        white-space: pre-line;
    }
    .results_row .-details {
        display: flex;
        font-size: 14px;
        opacity: 0.6;
    }
    .results_row .-details > * {
        padding-right: 16px;
    }
    .results_row .-details > * > *:first-child {
        padding-right: 4px;
    }
    /* as badges de tipo do service-select vêm do css global (badge_service, badge_package...);
       cobrança e assinatura não existem lá, então ficam locais mesmo */
    .badge_category-link {
        background-color: #1e88e5;
        margin-left: 4px;
    }
    .badge_category-subscription {
        background-color: #c78604;
        margin-left: 4px;
    }
</style>
